import React, {useMemo} from 'react'
import {Item, Menu} from 'react-contexify'
import {FolderIcon} from '@/ui/components/FileIcon'
import {Pin, usePinActions} from '../../pins'
import type {ShareView} from '../../sharing'
import {ACCESS_LABELS} from '../../sharing/constants'
import {sharedWithLabel} from '../../sharing/format'
import {PinnedName} from './PinnedName'
import {FileTableSkeleton} from './FileTableSkeleton'
import {
  FILE_TABLE_CELL_CONTENT_CLASS,
  fileTableCellContentStyle,
  fileTableCellStyle,
  fileTableHeaderCellStyle,
  fileTableHeaderRowStyle,
  fileTableStyle,
} from './fileTableStyles'

/**
 * The rows that stand for shared folders but are not files: the Shared row at the top of My Files,
 * and the shared folders listed inside it. They look like folder rows (same cells, the shared-folder
 * tint, the grey person-folder icon) and open like them, on a double-click; they are never part of
 * the file selection.
 */

/** Their context menu: Open, and Pin to top / Unpin for a shared folder. */
export const SHARED_ROW_MENU_ID = 'fileTableSharedRowMenu'

/** What the menu acts on, passed as its `props` when it is shown. */
export interface SharedRowMenuTarget {
  open: () => void
  /** The pins that stand for it: Pin to top adds the first, Unpin removes them all. Empty: Shared. */
  candidates: Pin[]
}

export function SharedRowMenu() {
  const {available, isPinned, pin, unpin} = usePinActions()
  const target = (props: unknown) => props as SharedRowMenuTarget | undefined
  const pinnable = (props: unknown) => available && !!target(props)?.candidates.length
  return (
    <Menu id={SHARED_ROW_MENU_ID} className='context-menu'>
      <Item id='shared-row-open' onClick={({props}) => target(props)?.open()}>
        Open
      </Item>
      <Item
        id='shared-row-pin'
        hidden={({props}) => !pinnable(props) || isPinned(...target(props)!.candidates)}
        onClick={({props}) => {
          const candidates = target(props)?.candidates
          if (candidates?.length) pin(candidates[0])
        }}
      >
        Pin to top
      </Item>
      <Item
        id='shared-row-unpin'
        hidden={({props}) => !pinnable(props) || !isPinned(...target(props)!.candidates)}
        onClick={({props}) => unpin(...(target(props)?.candidates ?? []))}
      >
        Unpin
      </Item>
    </Menu>
  )
}

interface RowBehaviour {
  /** Opens it: the second click of a double-click, or Enter. */
  onOpen: () => void
  /** A single click: the browser drops the file selection. */
  onSelect?: () => void
  /** Right-click. Absent in the file-pick dialog, which has no menus. */
  onContextMenu?: (e: React.MouseEvent) => void
}

/** Click, keyboard and menu handling of a row that is not a file. Focus shows it as clicked. */
const rowProps = ({onOpen, onSelect, onContextMenu}: RowBehaviour) => ({
  tabIndex: 0,
  onClick: (e: React.MouseEvent) => {
    e.stopPropagation()
    if (e.detail >= 2) {
      onOpen()
    } else {
      onSelect?.()
    }
  },
  onDoubleClick: (e: React.MouseEvent) => e.stopPropagation(),
  onKeyDown: (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onOpen()
    }
  },
  onContextMenu: onContextMenu
    ? (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        onContextMenu(e)
      }
    : undefined,
})

const Cell: React.FC<{children?: React.ReactNode}> = ({children}) => (
  <td style={fileTableCellStyle}>
    <div className={FILE_TABLE_CELL_CONTENT_CLASS} style={fileTableCellContentStyle}>
      {children}
    </div>
  </td>
)

/** The name cell of a shared folder, laid out as FileNameCell lays out a folder's. */
const SharedName: React.FC<{name: string; pinned?: boolean; badge?: React.ReactNode}> = ({
  name,
  pinned,
  badge,
}) => (
  <div className='ob-file-name-cell'>
    <FolderIcon />
    <PinnedName pinned={!!pinned}>
      <div className='ob-file-name-text' style={{paddingLeft: '5px'}} title={name}>
        {name}
      </div>
    </PinnedName>
    {badge}
  </div>
)

/**
 * The Shared row, first at the top of My Files: a folder row whose other cells are empty. One per
 * visible column of the file table, so it lines up with the files.
 */
export const SharedRootRow: React.FC<RowBehaviour & {columnCount: number}> = ({
  columnCount,
  ...behaviour
}) => (
  <tr
    data-testid='files-shared-row'
    className='ob-file-row--virtual ob-file-row--shared'
    {...rowProps(behaviour)}
  >
    {Array.from({length: columnCount}, (_, index) => (
      <Cell key={index}>{index === 0 ? <SharedName name='Shared' /> : null}</Cell>
    ))}
  </tr>
)

interface SharedFolderListProps {
  /** Shared with the user, and shared by them: one list. */
  shares: ShareView[]
  isLoading: boolean
  /** The shared folders pinned to the top of the list. */
  pinnedIds: ReadonlySet<string>
  /** Browse it, or for the owner, show it where it lives in My Files. */
  onOpen: (share: ShareView) => void
  /** Absent in the file-pick dialog. */
  onContextMenu?: (e: React.MouseEvent, share: ShareView) => void
}

const HEADERS = ['Name', 'Owner', 'Access']

const nameOf = (share: ShareView) => share.name || share.folderPath

/** Pinned first, then by name, as folders are sorted. */
const sortShares = (shares: ShareView[], pinnedIds: ReadonlySet<string>) =>
  [...shares].sort((a, b) => {
    const pinned = Number(pinnedIds.has(b.shareId)) - Number(pinnedIds.has(a.shareId))
    return (
      pinned || nameOf(a).localeCompare(nameOf(b), undefined, {numeric: true, sensitivity: 'base'})
    )
  })

/**
 * The Shared virtual folder: the shared folders as rows of the Files table (name, owner, access, and
 * "Being moved" while the owner moves one), striped in two blues.
 */
export const SharedFolderList: React.FC<SharedFolderListProps> = ({
  shares,
  isLoading,
  pinnedIds,
  onOpen,
  onContextMenu,
}) => {
  const sorted = useMemo(() => sortShares(shares, pinnedIds), [shares, pinnedIds])

  if (isLoading) {
    return (
      <div style={{width: '100%'}}>
        <FileTableSkeleton rowCount={3} />
      </div>
    )
  }

  return (
    <div className='table-responsive ob-file-table'>
      <table
        className='table table-flush table-row-bordered align-middle gs-0 ob-shared-list'
        style={fileTableStyle}
        data-testid='shared-folders-list'
      >
        <thead>
          <tr className='fw-bolder text-muted' style={fileTableHeaderRowStyle}>
            {HEADERS.map((header) => (
              <th key={header} style={fileTableHeaderCellStyle}>
                <div className='ob-file-table-header-label'>{header}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={HEADERS.length} className='ob-shared-list__empty'>
                No folders are shared with you yet. When someone shares one, it appears here.
              </td>
            </tr>
          ) : (
            sorted.map((share) => {
              const owner = share.access === 'owner'
              return (
                <tr
                  key={share.shareId}
                  data-testid={`shared-folder-${share.shareId}`}
                  className='ob-file-row--virtual ob-file-row--shared'
                  {...rowProps({
                    onOpen: () => onOpen(share),
                    onContextMenu: onContextMenu ? (e) => onContextMenu(e, share) : undefined,
                  })}
                >
                  <Cell>
                    <SharedName
                      name={nameOf(share)}
                      pinned={pinnedIds.has(share.shareId)}
                      badge={
                        share.moving ? (
                          <span
                            className='ob-shared-folder-badge'
                            data-testid='shared-folder-moving'
                          >
                            Being moved
                          </span>
                        ) : undefined
                      }
                    />
                  </Cell>
                  <Cell>
                    <span
                      className='ob-shared-list__meta'
                      title={owner ? undefined : share.ownerName}
                    >
                      {owner ? 'You' : share.ownerName || 'Another user'}
                    </span>
                  </Cell>
                  <Cell>
                    <span
                      className='ob-shared-list__meta'
                      title={owner ? sharedWithLabel(share.memberCount) : undefined}
                    >
                      {ACCESS_LABELS[share.access]}
                    </span>
                  </Cell>
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}
